/**
 * V2 Bravo — the recognition gesture. Full design: docs/BRAVO.md.
 *
 * A bravo is a scarce one-tap acknowledgement given to someone else's
 * yoink. It carries no score and appears on no leaderboard, which is
 * load-bearing rather than decorative: yoinks are honour-system, so
 * earning-by-yoinking would be farmable if a farmed bravo bought
 * anything. It buys nothing, so there is no market.
 *
 * Scarcity is banked server-side (supabase/migrations/20260727_bravos.sql):
 * one earned per yoink, capped at 10, three granted at signup. The client
 * never computes a balance — it reads profiles.bravo_balance and lets the
 * triggers do the arithmetic, so two devices can't double-spend.
 *
 * Degradation: until that migration is applied by hand every read 404s.
 * The first 404 sets `unavailable` for the session and every surface
 * renders nothing at all — no error, no empty state. Same pattern as
 * lists and the unread bell.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { icon } from '../../ui/icons.js';

export const BRAVOS_TABLE = 'yoink_bravos';

/** Above this many, the account page shows a numeral instead of a row. */
export const BRAVO_ROW_MAX = 5;

let unavailable = false;

/** Exported for tests — the session flag is module state by design. */
export function resetBravoAvailability() {
  unavailable = false;
}

export function isBravoUnavailable() {
  return unavailable;
}

/** A missing table/RPC is the un-applied migration, not a failure to report. */
function noteError(err) {
  if (/HTTP 40[04]/.test(err?.message || '')) unavailable = true;
  return null;
}

/**
 * Have I already bravoed this yoink? Null means "don't know" (signed out,
 * or the read failed) and the caller renders the count alone.
 */
export async function fetchMine(yoinkId, userId) {
  if (unavailable || !yoinkId || !userId) return null;
  const { data, error } = await gateway.rawQuery(BRAVOS_TABLE, {
    select: 'yoink_id',
    filters: { yoink_id: `eq.${yoinkId}`, giver_id: `eq.${userId}` },
    limit: 1,
  });
  if (error) return noteError(error);
  return Array.isArray(data) && data.length > 0;
}

/**
 * Bravos this user has left to give. Read straight off the profile row —
 * the triggers are the single source of truth.
 */
export async function fetchBalance(userId) {
  if (unavailable || !userId) return null;
  const { data, error } = await gateway.rawQuery(gateway.TABLES.PROFILES, {
    select: 'bravo_balance',
    filters: { id: `eq.${userId}` },
    limit: 1,
  });
  if (error) return noteError(error);
  const row = Array.isArray(data) ? data[0] : null;
  return row && typeof row.bravo_balance === 'number' ? row.bravo_balance : null;
}

/**
 * Give one. The server re-checks the balance and the not-your-own rule, so
 * a stale client can be refused; the thrown message is already
 * human-readable ("No bravos left to give.").
 */
export async function giveBravo(yoinkId, userId) {
  const { error } = await gateway.rawInsert(BRAVOS_TABLE, {
    body: { yoink_id: yoinkId, giver_id: userId },
  });
  if (error) {
    noteError(error);
    throw error;
  }
}

/** Take it back. The trigger refunds the balance and decrements the count. */
export async function takeBackBravo(yoinkId, userId) {
  const { error } = await gateway.rawDelete(BRAVOS_TABLE, {
    filters: { yoink_id: `eq.${yoinkId}`, giver_id: `eq.${userId}` },
  });
  if (error) {
    noteError(error);
    throw error;
  }
}

/**
 * The received count. Zero renders NOTHING — never a grey "0" — and one
 * glyph carries any number, never a cluster (repeated marks turn to mush
 * at 18px and start to look like a score).
 */
export function bravoCountHtml(count) {
  const n = Number(count) || 0;
  if (unavailable || n <= 0) return '';
  return `<span class="bravo-count">${icon('bravo', 18)}<span>${n} bravo${n === 1 ? '' : 's'}</span></span>`;
}

/**
 * The bar under a yoink: the button when you're allowed to give, the count
 * when there is one. Own yoinks and signed-out visitors get the count only.
 *
 * @param {object} o
 * @param {number} o.count    bravos received
 * @param {boolean} o.mine    have I given one
 * @param {number|null} o.balance  my remaining bravos
 * @param {boolean} o.canGive  signed in, not my own yoink, not read-only
 * @param {boolean} o.pending  a give/take is in flight
 * @param {string} o.flash    transient "N left to give", cleared by the caller
 */
export function bravoBarHtml({
  count = 0, mine = false, balance = null, canGive = false, pending = false, flash = '',
}) {
  if (unavailable) return '';
  const countHtml = bravoCountHtml(count);
  if (!canGive) return countHtml ? `<div class="yoink-bravos">${countHtml}</div>` : '';

  const empty = !mine && balance !== null && balance <= 0;
  const cls = ['bravo-btn'];
  if (mine) cls.push('bravo-btn-given');
  if (empty) cls.push('bravo-btn-empty');

  // The button never carries the balance — a count on the control is an
  // ambient pressure gauge. The empty state says what happens next and
  // does NOT tell anyone to go log something.
  const helper = empty
    ? '<span class="bravo-helper">You’ll have another after your next yoink.</span>'
    : '';

  return `
    <div class="yoink-bravos">
      <button type="button" class="${cls.join(' ')}" data-bravo
        ${pending || empty ? 'disabled' : ''}
        aria-pressed="${mine ? 'true' : 'false'}"
        aria-label="${mine ? 'Take back your bravo' : 'Give a bravo'}">
        ${icon('bravo', 17)}<span>Bravo</span>
      </button>
      ${countHtml}
      ${flash ? `<span class="bravo-flash" role="status">${escapeHtml(flash)}</span>` : helper}
      <span class="v2-form-error" data-bravo-error role="alert"></span>
    </div>`;
}

/**
 * What's left, said once, right after you spend or reclaim one.
 *
 * The balance is deliberately absent from the button (a count on a control
 * is an ambient pressure gauge) and it lives on the account page and your
 * own journal — but a tester who had just given two asked where to see the
 * bank, which is the moment the question actually arises. This answers it
 * there and then fades, so it stays information about an action you took
 * rather than a standing meter.
 *
 * @param {'gave'|'took-back'} action
 * @param {number|null} balance
 */
export function bravoFlashText(action, balance) {
  if (typeof balance !== 'number') return '';
  if (action === 'took-back') {
    return balance === 1 ? 'Back in your bank — 1 to give.' : `Back in your bank — ${balance} to give.`;
  }
  // Giving your last one leaves the button in its "given" state, so the
  // persistent empty helper never renders — this has to carry that news.
  if (balance <= 0) return 'That was your last — you’ll have another after your next yoink.';
  return balance === 1 ? '1 left to give.' : `${balance} left to give.`;
}

/**
 * Your own journal's quiet line. Own profile only, and silent at zero —
 * a journal page is not the place to be told what you cannot do.
 */
export function bravoJournalLineHtml(balance) {
  if (unavailable || typeof balance !== 'number' || balance <= 0) return '';
  return `<p class="bravo-journal-line">${icon('bravo', 18)}
    <span>${balance} bravo${balance === 1 ? '' : 's'} to give</span></p>`;
}

/**
 * The line on the post-create banner — the one placement that teaches the
 * mechanic, because it is the instant "I went somewhere" becomes "I can
 * recognize someone". Silent when the balance is unknown or spent.
 */
export function bravoEarnedHtml(balance) {
  if (unavailable || typeof balance !== 'number' || balance <= 0) return '';
  return `<span class="bravo-earned">${icon('bravo', 16)}
    <span>${balance} bravo${balance === 1 ? '' : 's'} to give</span></span>`;
}

/**
 * The account-page readout. A row of flowers up to BRAVO_ROW_MAX, then one
 * glyph and a numeral. Copy is "N to give" — never "N of M", because a
 * denominator implies a fixed allotment and a refill schedule that does
 * not exist.
 */
export function bravoBalanceHtml(balance) {
  if (unavailable || typeof balance !== 'number') return '';
  if (balance <= 0) {
    return `<div class="bravo-balance bravo-balance-empty">
      <span>No bravos to give right now — you’ll have another after your next yoink.</span>
    </div>`;
  }
  const marks = balance <= BRAVO_ROW_MAX
    ? Array.from({ length: balance }, () => icon('bravo', 26)).join('')
    : icon('bravo', 26);
  return `<div class="bravo-balance">
    <span class="bravo-balance-marks">${marks}</span>
    <span class="bravo-balance-text">${balance} to give</span>
  </div>`;
}
